import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppDropdown from '../../components/AppDropdown'
import AppPagination from '../../components/AppPagination'
import {
  fetchChannelAssets,
  fetchChannelDetails,
  fetchChatHubKpis,
  fetchChatPresence,
  fetchChannelMessages,
  fetchConversations,
  fetchUsersForThread,
  markChannelRead,
  sendChannelMessage,
  startDirectThread,
  uploadChannelAttachment,
  createGroupChannel,
  addChannelMember,
  removeChannelMember,
  renameChannel,
} from './api/chatApi'
import BridgeActionCenterModal from '../dashboard/calendar/components/BridgeActionCenterModal'
import { resolveMediaUrl } from '../../utils/mediaUrl'
import './communication.css'

const HUB_PAGE_SIZE = 6
const THREAD_PAGE_SIZE = 10
const ASSETS_PAGE_SIZE = 4
const FALLBACK_POLL_MS = 3000
const PRESENCE_POLL_MS = 10000
const THREAD_SYNC_POLL_MS = 2500
const MESSAGE_BATCH_LIMIT = 80
const EMOJI_OPTIONS = ['😀', '👍', '🎯', '✅', '🔥', '📌', '💡', '🙏', '🎉', '🚀', '📎', '❤️']
const EMPTY_LIST = []
const TYPING_STOP_DEBOUNCE_MS = 2000
const TYPING_NAME_MAX_INLINE = 2

function toSafePositiveInt(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return Math.floor(parsed)
}

function getConversationLastMessageId(item) {
  return toSafePositiveInt(item?.last_message_id || item?.lastMessageId || 0)
}

function arePresenceMapsEqual(a = {}, b = {}) {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    const left = a[key] || {}
    const right = b[key] || {}
    if (Boolean(left.isOnline) !== Boolean(right.isOnline)) return false
    if (String(left.lastHeartbeatAt || '') !== String(right.lastHeartbeatAt || '')) return false
  }
  return true
}

function getStoredUser() {
  const raw = localStorage.getItem('user')
  if (!raw) return { id: null, name: 'You' }
  try {
    const parsed = JSON.parse(raw)
    const id = Number(parsed?.id ?? parsed?.user_id ?? parsed?.userId ?? 0)
    return {
      id: Number.isFinite(id) && id > 0 ? id : null,
      name: parsed?.name || parsed?.userName || 'You',
    }
  } catch {
    return { id: null, name: 'You' }
  }
}

function formatClock(value) {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '--'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatMessageTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDateHeading(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((today.getTime() - messageDay.getTime()) / 86400000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'long' })
  }
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
}

function toDateKey(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

function buildMessageTimelineRows(messages) {
  const rows = []
  let lastKey = ''
  for (const message of messages) {
    const dateKey = toDateKey(message?.created_at || message?.createdAt)
    if (dateKey && dateKey !== lastKey) {
      rows.push({
        type: 'day',
        key: `day-${dateKey}`,
        label: formatDateHeading(message?.created_at || message?.createdAt),
      })
      lastKey = dateKey
    }
    rows.push({
      type: 'message',
      key: `msg-${message?.id}`,
      message,
    })
  }
  return rows
}

function toConversationType(item) {
  const t = String(item?.channel_type || '').toUpperCase()
  if (t === 'DIRECT') return 'Direct Message'
  if (t === 'GROUP') return 'Group Channel'
  return 'Project Channel'
}

function toConversationName(item) {
  const t = String(item?.channel_type || '').toUpperCase()
  if (t === 'DIRECT') return item?.other_user_name || item?.channel_name || 'Direct Thread'
  if (t === 'GROUP') return item?.channel_name || 'Group Channel'
  return item?.channel_name || 'Project Channel'
}

function toConversationAvatar(item) {
  if (String(item?.channel_type || '').toUpperCase() === 'DIRECT') {
    return resolveMediaUrl(item?.other_user_avatar)
  }
  return ''
}

function toSnippet(item, currentUserId, currentUserName) {
  if (!item?.last_message_content) return 'No messages yet'
  const isSelf = Number(item?.last_message_sender_id) === Number(currentUserId)
  const senderName = isSelf ? 'You' : (item?.last_message_sender_name || currentUserName || 'User')
  return `${senderName}: ${stripFormattingTokens(item.last_message_content)}`
}

function snippetFromSelf(item, currentUserId) {
  return Number(item?.last_message_sender_id) === Number(currentUserId)
}

function applyFormattingToDraft(rawText, isBold, isItalic) {
  const value = String(rawText || '').trim()
  if (!value) return ''
  let next = value
  if (isBold) next = `**${next}**`
  if (isItalic) next = `_${next}_`
  return next
}

function stripFormattingTokens(text) {
  return String(text || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
}

function formatRelativeUpdated(value) {
  if (!value) return 'No recent activity'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'No recent activity'
  const deltaSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (deltaSeconds < 60) return 'Updated just now'
  if (deltaSeconds < 3600) return `Updated ${Math.floor(deltaSeconds / 60)} mins ago`
  if (deltaSeconds < 86400) return `Updated ${Math.floor(deltaSeconds / 3600)} hrs ago`
  if (deltaSeconds < 172800) return 'Updated yesterday'
  return `Updated ${Math.floor(deltaSeconds / 86400)} days ago`
}

function fallbackInitials(name) {
  const parts = String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
  const letters = parts.map((part) => part[0]).join('').toUpperCase()
  return letters || 'U'
}

function isImageAsset(pathOrName) {
  const value = String(pathOrName || '').toLowerCase()
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(value)
}

function AvatarBadge({
  src,
  name,
  icon = 'person',
  sizeClass = 'w-10 h-10',
  textClass = 'text-sm',
  showPresence = false,
  isOnline = false,
}) {
  const mediaUrl = resolveMediaUrl(src)
  return (
    <div className="relative shrink-0">
      <div className={`${sizeClass} rounded-full bg-surface-container-highest overflow-hidden flex items-center justify-center`}>
        {mediaUrl ? (
          <img className="w-full h-full object-cover" src={mediaUrl} alt={name || 'User'} />
        ) : icon === 'initials' ? (
          <span className={`font-bold text-on-surface-variant ${textClass}`}>{fallbackInitials(name)}</span>
        ) : (
          <span className="material-symbols-outlined text-on-surface-variant">{icon}</span>
        )}
      </div>
      {showPresence ? (
        <span
          className={`communication-presence-dot ${isOnline ? 'communication-presence-dot--online' : 'communication-presence-dot--offline'}`}
          aria-label={isOnline ? 'Online' : 'Offline'}
        ></span>
      ) : null}
    </div>
  )
}

function renderFormattedMessage(content) {
  const value = String(content || '').replace(/\r\n/g, '\n')
  const urlPattern = /(https?:\/\/[^\s]+)/gi

  const renderLinkifiedText = (text, keyPrefix) => {
    const parts = String(text || '').split(urlPattern)
    return parts.map((part, idx) => {
      if (!part) return null
      if (/^https?:\/\/[^\s]+$/i.test(part)) {
        const cleanUrl = part.replace(/[),.;!?]+$/g, '')
        const trailing = part.slice(cleanUrl.length)
        return (
          <span key={`${keyPrefix}-url-wrap-${idx}`}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 break-all"
            >
              {cleanUrl}
            </a>
            {trailing ? <span>{trailing}</span> : null}
          </span>
        )
      }
      return <span key={`${keyPrefix}-txt-${idx}`}>{part}</span>
    })
  }

  const lines = value.split('\n')
  return lines.map((line, lineIdx) => {
    const tokens = line.split(/(\*\*[^*]+\*\*|_[^_]+_)/g)
    return (
      <span key={`line-${lineIdx}`}>
        {tokens.map((part, idx) => {
          if (!part) return null
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={`b-${lineIdx}-${idx}`}>
                {renderLinkifiedText(part.slice(2, -2), `b-${lineIdx}-${idx}`)}
              </strong>
            )
          }
          if (part.startsWith('_') && part.endsWith('_')) {
            return (
              <em key={`i-${lineIdx}-${idx}`}>
                {renderLinkifiedText(part.slice(1, -1), `i-${lineIdx}-${idx}`)}
              </em>
            )
          }
          return <span key={`p-${lineIdx}-${idx}`}>{renderLinkifiedText(part, `p-${lineIdx}-${idx}`)}</span>
        })}
        {lineIdx < lines.length - 1 ? <br /> : null}
      </span>
    )
  })
}

function formatTypingUsersLabel(users) {
  const names = (Array.isArray(users) ? users : []).filter(Boolean)
  if (!names.length) return ''
  if (names.length === 1) return `${names[0]} is typing...`
  if (names.length <= TYPING_NAME_MAX_INLINE) return `${names.join(', ')} are typing...`
  const inline = names.slice(0, TYPING_NAME_MAX_INLINE).join(', ')
  const moreCount = names.length - TYPING_NAME_MAX_INLINE
  return `${inline} +${moreCount} more are typing...`
}

function ConversationThreadScreen({
  currentUserId,
  currentUserName,
  channelId,
  onBackToHub,
  onRefreshConversations,
  onReadAck,
  onOpenScheduleMeeting,
}) {
  const [channel, setChannel] = useState(null)
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [typingUsersById, setTypingUsersById] = useState({})
  const [wsConnected, setWsConnected] = useState(() => {
    if (typeof window === 'undefined') return false
    return Boolean(window.__chatWsConnected)
  })
  const [boldActive, setBoldActive] = useState(false)
  const [italicActive, setItalicActive] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [assets, setAssets] = useState([])
  const [assetsPage, setAssetsPage] = useState(1)
  const [assetsTotal, setAssetsTotal] = useState(0)
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [initialAnchor, setInitialAnchor] = useState({ mode: 'bottom', messageId: null })
  const [unreadMarker, setUnreadMarker] = useState(null)
  const [expandedReadMessageId, setExpandedReadMessageId] = useState(0)
  const [readStateByUser, setReadStateByUser] = useState({})
  const [presenceByUser, setPresenceByUser] = useState({})
  const [presenceRefreshTick, setPresenceRefreshTick] = useState(0)
  const [showAddMember, setShowAddMember] = useState(false)
  const [addMemberQuery, setAddMemberQuery] = useState('')
  const [addMemberResults, setAddMemberResults] = useState([])
  const [addMemberLoading, setAddMemberLoading] = useState(false)
  const [addMemberError, setAddMemberError] = useState('')
  const [addMemberSuccess, setAddMemberSuccess] = useState('')
  const [removingMemberId, setRemovingMemberId] = useState(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editNameText, setEditNameText] = useState('')

  const lastSavedNameRef = useRef(channel?.name || '')
  const isSavingNameRef = useRef(false)
  const bypassBlurRef = useRef(false)

  useEffect(() => {
    if (channel?.name) {
      lastSavedNameRef.current = channel.name
    }
  }, [channel?.name])

  const typingTimerRef = useRef(null)
  const typingSentRef = useRef(false)
  const messageViewportRef = useRef(null)
  const messageNodeRefs = useRef(new Map())
  const hasAutoAnchoredRef = useRef(false)
  const hasMarkedInitialReadRef = useRef(false)
  const lastMarkedReadMessageIdRef = useRef(0)
  const persistUnreadMarkerUntilExitRef = useRef(false)
  const exitCommitInFlightRef = useRef(false)
  const exitCommittedMessageIdRef = useRef(0)
  const latestMessageIdRef = useRef(0)
  const shouldStickToBottomRef = useRef(true)
  const fileInputRef = useRef(null)
  const textAreaRef = useRef(null)

  const mergeMessages = useCallback((nextItems) => {
    setMessages((prev) => {
      const bucket = new Map()
      prev.forEach((item) => bucket.set(Number(item?.id), item))
      nextItems.forEach((item) => bucket.set(Number(item?.id), item))
      const merged = Array.from(bucket.values()).sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
      const newestId = Number(merged[merged.length - 1]?.id || 0)
      if (newestId > 0) latestMessageIdRef.current = newestId
      return merged
    })
  }, [])

  const setMessageNodeRef = useCallback((messageId, node) => {
    const key = Number(messageId || 0)
    if (!key) return
    if (!node) {
      messageNodeRefs.current.delete(key)
      return
    }
    messageNodeRefs.current.set(key, node)
  }, [])

  const updateCurrentUserReadPointer = useCallback((lastReadMessageId) => {
    const normalized = Number(lastReadMessageId || 0)
    if (!normalized) return
    setReadStateByUser((prev) => ({
      ...prev,
      [Number(currentUserId || 0)]: {
        ...(prev[Number(currentUserId || 0)] || {}),
        lastReadMsgId: normalized,
        readAt: new Date().toISOString(),
      },
    }))
  }, [currentUserId])

  const markLatestAsRead = useCallback(async (rows) => {
    if (!channelId || !Array.isArray(rows) || rows.length === 0) return
    const latest = rows[rows.length - 1]
    const latestId = Number(latest?.id || 0)
    if (!latestId) return
    if (latestId <= Number(lastMarkedReadMessageIdRef.current || 0)) return

    try {
      await markChannelRead(channelId, latestId)
      lastMarkedReadMessageIdRef.current = latestId
      updateCurrentUserReadPointer(latestId)
      onReadAck?.(channelId, latestId)
      onRefreshConversations?.()
      window.dispatchEvent(new CustomEvent('chat-unread-updated'))
    } catch {
      // best effort
    }
  }, [channelId, onReadAck, onRefreshConversations, updateCurrentUserReadPointer])

  const commitReadOnExit = useCallback(async (reason = 'exit') => {
    if (!channelId) return false
    const latestLoadedId = Number(latestMessageIdRef.current || 0)
    if (!latestLoadedId) return false
    const lastCommitted = Math.max(
      Number(lastMarkedReadMessageIdRef.current || 0),
      Number(exitCommittedMessageIdRef.current || 0),
    )
    if (latestLoadedId <= lastCommitted) return false
    if (exitCommitInFlightRef.current) return false

    exitCommitInFlightRef.current = true
    if (import.meta.env.DEV) {
      console.debug('[chat] commitReadOnExit:start', {
        channelId,
        reason,
        latestLoadedId,
        lastCommitted,
      })
    }
    try {
      await markChannelRead(channelId, latestLoadedId)
      lastMarkedReadMessageIdRef.current = latestLoadedId
      exitCommittedMessageIdRef.current = latestLoadedId
      updateCurrentUserReadPointer(latestLoadedId)
      onReadAck?.(channelId, latestLoadedId)
      onRefreshConversations?.()
      window.dispatchEvent(new CustomEvent('chat-unread-updated'))
      if (import.meta.env.DEV) {
        console.debug('[chat] commitReadOnExit:success', { channelId, latestLoadedId, reason })
      }
      return true
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('[chat] commitReadOnExit:failed', {
          channelId,
          latestLoadedId,
          reason,
          message: String(error?.message || ''),
        })
      }
      return false
    } finally {
      exitCommitInFlightRef.current = false
    }
  }, [channelId, onReadAck, onRefreshConversations, updateCurrentUserReadPointer])

  const handleBackToHub = useCallback(() => {
    commitReadOnExit('back').finally(() => {
      onBackToHub?.()
    })
  }, [commitReadOnExit, onBackToHub])

  const loadThread = useCallback(async () => {
    if (!channelId) return
    setLoading(true)
    try {
      const channelData = await fetchChannelDetails(channelId)
      setChannel(channelData || null)

      const members = Array.isArray(channelData?.members) ? channelData.members : []
      const initialReadState = {}
      members.forEach((member) => {
        const memberId = Number(member?.id || 0)
        if (!memberId) return
        initialReadState[memberId] = {
          id: memberId,
          name: member?.name || 'User',
          avatar: member?.profile_photo_path || '',
          role: member?.role || 'MEMBER',
          lastReadMsgId: Number(member?.last_read_msg_id || 0),
          readAt: member?.read_at || null,
        }
      })
      setReadStateByUser(initialReadState)

      const currentUserLastRead = Number(
        channelData?.current_user_last_read_msg_id
        ?? initialReadState[Number(currentUserId || 0)]?.lastReadMsgId
        ?? 0,
      )

      let messageData = await fetchChannelMessages(channelId, { limit: MESSAGE_BATCH_LIMIT })
      let rows = Array.isArray(messageData?.messages) ? [...messageData.messages].reverse() : []
      let hasMore = Boolean(messageData?.hasMore)

      while (
        hasMore
        && rows.length > 0
        && Number(rows[0]?.id || 0) > currentUserLastRead + 1
      ) {
        const beforeId = Number(rows[0]?.id || 0)
        if (!beforeId) break
        const olderData = await fetchChannelMessages(channelId, { limit: MESSAGE_BATCH_LIMIT, beforeId })
        const olderRows = Array.isArray(olderData?.messages) ? [...olderData.messages].reverse() : []
        if (!olderRows.length) break
        const merged = new Map()
        olderRows.forEach((item) => merged.set(Number(item?.id || 0), item))
        rows.forEach((item) => merged.set(Number(item?.id || 0), item))
        rows = Array.from(merged.values()).sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
        hasMore = Boolean(olderData?.hasMore)
      }

      setMessages(rows)
      latestMessageIdRef.current = Number(rows[rows.length - 1]?.id || 0)

      const newestId = Number(rows[rows.length - 1]?.id || 0)
      const hasUnreadFromOthers = rows.some(
        (item) => Number(item?.id || 0) > currentUserLastRead && Number(item?.sender_id || 0) !== Number(currentUserId || 0),
      )
      if (rows.length && newestId > currentUserLastRead && hasUnreadFromOthers) {
        const firstUnread = rows.find(
          (item) => Number(item?.id || 0) > currentUserLastRead && Number(item?.sender_id || 0) !== Number(currentUserId || 0),
        ) || null
        const unreadCount = rows.filter(
          (item) => Number(item?.id || 0) > currentUserLastRead && Number(item?.sender_id || 0) !== Number(currentUserId || 0),
        ).length
        if (firstUnread && !persistUnreadMarkerUntilExitRef.current) {
          setUnreadMarker({
            messageId: Number(firstUnread.id),
            count: unreadCount,
          })
          setInitialAnchor({
            mode: 'unread',
            messageId: Number(firstUnread.id),
          })
          persistUnreadMarkerUntilExitRef.current = true
        }
      } else {
        if (!persistUnreadMarkerUntilExitRef.current) {
          setUnreadMarker(null)
          setInitialAnchor({ mode: 'bottom', messageId: null })
        }
      }
    } finally {
      setLoading(false)
    }
  }, [channelId, currentUserId])

  const loadAssets = useCallback(async (targetPage = 1, append = false) => {
    if (!channelId) return
    setAssetsLoading(true)
    try {
      const data = await fetchChannelAssets(channelId, { page: targetPage, pageSize: ASSETS_PAGE_SIZE })
      const rows = Array.isArray(data?.items) ? data.items : []
      setAssets((prev) => (append ? [...prev, ...rows] : rows))
      setAssetsTotal(Number(data?.total || 0))
      setAssetsPage(Number(data?.page || targetPage))
    } finally {
      setAssetsLoading(false)
    }
  }, [channelId])

  useEffect(() => {
    loadThread().catch(() => { })
  }, [loadThread])

  useEffect(() => {
    setAssets([])
    setAssetsPage(1)
    setAssetsTotal(0)
    setUploadError('')
    setExpandedReadMessageId(0)
    setReadStateByUser({})
    setPresenceByUser({})
    setUnreadMarker(null)
    setInitialAnchor({ mode: 'bottom', messageId: null })
    hasAutoAnchoredRef.current = false
    hasMarkedInitialReadRef.current = false
    lastMarkedReadMessageIdRef.current = 0
    persistUnreadMarkerUntilExitRef.current = false
    latestMessageIdRef.current = 0
    shouldStickToBottomRef.current = true
    messageNodeRefs.current.clear()
    loadAssets(1, false).catch(() => { })
  }, [channelId, loadAssets])

  useEffect(() => () => {
    commitReadOnExit('unmount').catch(() => { })
  }, [commitReadOnExit])

  useEffect(() => {
    const viewport = messageViewportRef.current
    if (!viewport) return
    if (!messages.length) return

    if (!hasAutoAnchoredRef.current) {
      if (initialAnchor.mode === 'unread' && initialAnchor.messageId) {
        const targetNode = messageNodeRefs.current.get(Number(initialAnchor.messageId))
        if (targetNode) {
          viewport.scrollTo({ top: Math.max(targetNode.offsetTop - 84, 0), behavior: 'auto' })
        } else {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' })
        }
      } else {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' })
      }
      hasAutoAnchoredRef.current = true
      if (!hasMarkedInitialReadRef.current) {
        if (initialAnchor.mode !== 'unread') {
          markLatestAsRead(messages).catch(() => { })
        }
        hasMarkedInitialReadRef.current = true
      }
      return
    }

    if (shouldStickToBottomRef.current) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' })
    }
  }, [initialAnchor, markLatestAsRead, messages])

  const handleRealtimePayload = useCallback((payload) => {
    if (Number(payload?.channelId || 0) !== Number(channelId)) return

    if (payload?.event === 'new_message' && payload?.message) {
      mergeMessages([payload.message])
      const senderId = Number(payload?.message?.sender_id || 0)
      if (senderId > 0) {
        setTypingUsersById((prev) => {
          if (!prev[senderId]) return prev
          const next = { ...prev }
          delete next[senderId]
          return next
        })
      }
      if (String(payload?.message?.message_type || '').toUpperCase() === 'FILE') {
        loadAssets(1, false).catch(() => { })
      }
      const incomingId = Number(payload?.message?.id || 0)
      if (incomingId > 0 && senderId !== Number(currentUserId)) {
        markChannelRead(channelId, incomingId)
          .then(() => {
            updateCurrentUserReadPointer(incomingId)
            onReadAck?.(channelId, incomingId)
            onRefreshConversations?.()
            window.dispatchEvent(new CustomEvent('chat-unread-updated'))
          })
          .catch(() => { })
      }
      onRefreshConversations?.()
      setPresenceRefreshTick((prev) => prev + 1)
      return
    }

    if (payload?.event === 'read_receipt') {
      const readerId = Number(payload?.userId || 0)
      const lastReadMsgId = Number(payload?.lastReadMsgId || 0)
      if (readerId > 0 && lastReadMsgId > 0) {
        setReadStateByUser((prev) => ({
          ...prev,
          [readerId]: {
            ...(prev[readerId] || {}),
            id: readerId,
            lastReadMsgId,
            readAt: new Date().toISOString(),
          },
        }))
      }
      setPresenceRefreshTick((prev) => prev + 1)
      return
    }

    if (payload?.event === 'typing' && Number(payload?.userId || 0) !== Number(currentUserId)) {
      const typingUserId = Number(payload?.userId || 0)
      if (typingUserId <= 0) return
      const name = String(payload?.userName || 'Someone')
      setTypingUsersById((prev) => ({
        ...prev,
        [typingUserId]: name,
      }))
      return
    }

    if (payload?.event === 'stop_typing' && Number(payload?.userId || 0) !== Number(currentUserId)) {
      const typingUserId = Number(payload?.userId || 0)
      if (typingUserId <= 0) return
      setTypingUsersById((prev) => {
        if (!prev[typingUserId]) return prev
        const next = { ...prev }
        delete next[typingUserId]
        return next
      })
      return
    }

    if (payload?.event === 'member_added') {
      loadThread().catch(() => { })
      onRefreshConversations?.()
      return
    }

    if (payload?.event === 'member_removed') {
      const removedUserId = Number(payload?.userId || 0)
      if (removedUserId === Number(currentUserId)) {
        onBackToHub?.()
      } else {
        loadThread().catch(() => { })
        onRefreshConversations?.()
      }
      return
    }

    if (payload?.event === 'channel_renamed') {
      const newName = String(payload?.name || '')
      if (newName) {
        setChannel((prev) => (prev ? { ...prev, name: newName } : null))
      }
      onRefreshConversations?.()
    }
  }, [channelId, currentUserId, loadAssets, loadThread, mergeMessages, onBackToHub, onReadAck, onRefreshConversations, updateCurrentUserReadPointer])

  const handleSaveChannelName = useCallback(async () => {
    const trimmed = editNameText.trim()
    if (!trimmed || trimmed === lastSavedNameRef.current || isSavingNameRef.current) {
      setIsEditingName(false)
      return
    }
    isSavingNameRef.current = true
    try {
      await renameChannel(channelId, trimmed)
      lastSavedNameRef.current = trimmed
      setChannel((prev) => (prev ? { ...prev, name: trimmed } : null))
      setIsEditingName(false)
      onRefreshConversations?.()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Rename channel failed:', err)
    } finally {
      isSavingNameRef.current = false
    }
  }, [channelId, editNameText, onRefreshConversations])

  useEffect(() => {
    const handleWsStatus = (event) => {
      const connected = Boolean(event?.detail?.connected)
      setWsConnected(connected)
      if (connected) {
        setPresenceRefreshTick((prev) => prev + 1)
      }
    }
    handleWsStatus({ detail: { connected: Boolean(window.__chatWsConnected) } })
    window.addEventListener('chat-ws-status', handleWsStatus)
    return () => window.removeEventListener('chat-ws-status', handleWsStatus)
  }, [])

  useEffect(() => {
    const handleGlobalWsEvent = (event) => {
      const payload = event?.detail
      if (!payload || typeof payload !== 'object') return
      handleRealtimePayload(payload)
    }
    window.addEventListener('chat-ws-event', handleGlobalWsEvent)
    return () => window.removeEventListener('chat-ws-event', handleGlobalWsEvent)
  }, [handleRealtimePayload])

  useEffect(() => {
    if (wsConnected) return undefined
    const poll = setInterval(() => {
      loadThread().catch(() => { })
    }, FALLBACK_POLL_MS)
    return () => clearInterval(poll)
  }, [loadThread, wsConnected])

  useEffect(() => {
    if (wsConnected) return undefined
    if (!channelId) return undefined
    const timer = setInterval(() => {
      fetchChannelMessages(channelId, { limit: 30 })
        .then((data) => {
          const rows = Array.isArray(data?.messages) ? [...data.messages].reverse() : []
          if (!rows.length) return
          const newestId = Number(rows[rows.length - 1]?.id || 0)
          if (newestId <= Number(latestMessageIdRef.current || 0)) return
          mergeMessages(rows)
          if (shouldStickToBottomRef.current) {
            markLatestAsRead(rows).catch(() => { })
          }
          onRefreshConversations?.()
        })
        .catch(() => { })
    }, THREAD_SYNC_POLL_MS)
    return () => clearInterval(timer)
  }, [channelId, markLatestAsRead, mergeMessages, onRefreshConversations, wsConnected])

  const sendTypingEvent = useCallback((eventName) => {
    const payload = {
      event: eventName,
      channelId: Number(channelId),
    }

    // Send through global chat socket owned by DashboardLayout
    window.dispatchEvent(new CustomEvent('chat-ws-send', { detail: payload }))
  }, [channelId])

  const handleTypingChange = useCallback((nextValue) => {
    if (nextValue.trim()) {
      if (!typingSentRef.current) {
        sendTypingEvent('typing')
        typingSentRef.current = true
      }
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => {
        sendTypingEvent('stop_typing')
        typingSentRef.current = false
      }, TYPING_STOP_DEBOUNCE_MS)
    } else if (typingSentRef.current) {
      sendTypingEvent('stop_typing')
      typingSentRef.current = false
    }
  }, [sendTypingEvent])

  useEffect(() => () => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
  }, [])

  const typingUsers = useMemo(
    () => Object.values(typingUsersById).filter(Boolean),
    [typingUsersById],
  )

  const submitMessage = useCallback(async () => {
    if (!channelId || !text.trim() || sending) return
    const payload = applyFormattingToDraft(text, boldActive, italicActive)
    if (!payload) return

    setSending(true)
    try {
      if (typingSentRef.current) {
        sendTypingEvent('stop_typing')
        typingSentRef.current = false
      }

      await sendChannelMessage(channelId, payload)
      setText('')
      setBoldActive(false)
      setItalicActive(false)
      setShowEmojiPicker(false)
      shouldStickToBottomRef.current = true
      await loadThread()
      onRefreshConversations?.()
    } finally {
      setSending(false)
    }
  }, [boldActive, channelId, italicActive, loadThread, onRefreshConversations, sendTypingEvent, sending, text])

  const members = useMemo(
    () => (Array.isArray(channel?.members) ? channel.members : EMPTY_LIST),
    [channel?.members],
  )
  const isChannelAdmin = useMemo(() => {
    const me = members.find((m) => Number(m?.id) === Number(currentUserId))
    return String(me?.role || '').toUpperCase() === 'ADMIN'
  }, [members, currentUserId])
  const memberReadStates = useMemo(
    () => members.map((member) => ({
      id: Number(member?.id || 0),
      name: member?.name || 'User',
      avatar: member?.profile_photo_path || '',
      role: member?.role || 'MEMBER',
      lastReadMsgId: Number(readStateByUser[Number(member?.id || 0)]?.lastReadMsgId ?? member?.last_read_msg_id ?? 0),
    })),
    [members, readStateByUser],
  )
  const recipientMembers = useMemo(
    () => memberReadStates.filter((member) => Number(member?.id || 0) !== Number(currentUserId || 0)),
    [currentUserId, memberReadStates],
  )
  const visiblePresenceUserIds = useMemo(
    () => Array.from(new Set(memberReadStates.map((member) => Number(member?.id || 0)).filter((id) => id > 0))),
    [memberReadStates],
  )
  const visiblePresenceUserIdsKey = useMemo(
    () => visiblePresenceUserIds.join(','),
    [visiblePresenceUserIds],
  )
  const timelineRows = useMemo(() => buildMessageTimelineRows(messages), [messages])
  const isDirect = String(channel?.channel_type || '').toUpperCase() === 'DIRECT'
  const directTargetMember = useMemo(
    () => members.find((member) => Number(member?.id) !== Number(currentUserId)) || null,
    [currentUserId, members],
  )
  const directTargetName = useMemo(() => {
    if (!isDirect) return ''
    return directTargetMember?.name || channel?.name || 'Direct Message'
  }, [channel?.name, directTargetMember, isDirect])
  const directTargetAvatar = useMemo(
    () => (isDirect ? resolveMediaUrl(directTargetMember?.profile_photo_path) : ''),
    [directTargetMember?.profile_photo_path, isDirect],
  )
  const refreshPresence = useCallback(async () => {
    if (!visiblePresenceUserIds.length) {
      setPresenceByUser((prev) => (Object.keys(prev).length ? {} : prev))
      return
    }
    try {
      const data = await fetchChatPresence(visiblePresenceUserIds)
      const rows = Array.isArray(data?.items) ? data.items : []
      const next = {}
      rows.forEach((item) => {
        const uid = Number(item?.userId || 0)
        if (!uid) return
        next[uid] = {
          isOnline: Boolean(item?.isOnline),
          lastHeartbeatAt: item?.lastHeartbeatAt || null,
        }
      })
      setPresenceByUser((prev) => (arePresenceMapsEqual(prev, next) ? prev : next))
    } catch {
      setPresenceByUser((prev) => (Object.keys(prev).length ? {} : prev))
    }
  }, [visiblePresenceUserIds, visiblePresenceUserIdsKey])

  useEffect(() => {
    refreshPresence().catch(() => { })
  }, [refreshPresence])

  useEffect(() => {
    if (!presenceRefreshTick) return
    refreshPresence().catch(() => { })
  }, [presenceRefreshTick, refreshPresence])

  useEffect(() => {
    if (!visiblePresenceUserIds.length) return undefined
    const timer = setInterval(() => {
      refreshPresence().catch(() => { })
    }, PRESENCE_POLL_MS)
    return () => clearInterval(timer)
  }, [refreshPresence, visiblePresenceUserIds.length, visiblePresenceUserIdsKey])

  const updatedLabel = useMemo(() => {
    const channelUpdated = channel?.updated_at || channel?.updatedAt
    if (channelUpdated) return formatRelativeUpdated(channelUpdated)
    const lastMessage = messages[messages.length - 1]
    return formatRelativeUpdated(lastMessage?.created_at || lastMessage?.createdAt)
  }, [channel?.updatedAt, channel?.updated_at, messages])
  const mentionQuery = useMemo(() => {
    if (isDirect) return ''
    const match = text.match(/(?:^|\s)@([a-zA-Z0-9._-]*)$/)
    return match ? match[1].toLowerCase() : ''
  }, [isDirect, text])
  const mentionSuggestions = useMemo(() => {
    if (isDirect) return []
    const options = members.filter((member) => Number(member?.id) !== Number(currentUserId))
    if (!mentionQuery) return options.slice(0, 6)
    return options
      .filter((member) => String(member?.name || '').toLowerCase().includes(mentionQuery))
      .slice(0, 6)
  }, [currentUserId, isDirect, members, mentionQuery])
  const showMentionMenu = !isDirect && mentionSuggestions.length > 0 && /(?:^|\s)@([a-zA-Z0-9._-]*)$/.test(text)

  const insertAtCursor = useCallback((insertText) => {
    const input = textAreaRef.current
    if (!input) {
      setText((prev) => `${prev}${insertText}`)
      return
    }
    const start = input.selectionStart ?? text.length
    const end = input.selectionEnd ?? text.length
    const next = `${text.slice(0, start)}${insertText}${text.slice(end)}`
    setText(next)
    requestAnimationFrame(() => {
      const nextCursor = start + insertText.length
      input.focus()
      input.setSelectionRange(nextCursor, nextCursor)
    })
  }, [text])

  const insertMention = useCallback((memberName) => {
    const mentionToken = `@${memberName}`
    const next = text.replace(/(?:^|\s)@([a-zA-Z0-9._-]*)$/, (match) => {
      const prefix = match.startsWith(' ') ? ' ' : ''
      return `${prefix}${mentionToken} `
    })
    setText(next)
    requestAnimationFrame(() => {
      textAreaRef.current?.focus()
    })
  }, [text])

  const handleAttachmentPick = useCallback(async (event) => {
    const file = event.target.files?.[0]
    if (!file || !channelId || uploadingAttachment) return
    setUploadingAttachment(true)
    setUploadError('')
    try {
      await uploadChannelAttachment(channelId, file)
      await loadThread()
      await loadAssets(1, false)
      onRefreshConversations?.()
      window.dispatchEvent(new CustomEvent('chat-unread-updated'))
    } catch (error) {
      const detailMessage = String(error?.response?.data?.detail?.message || error?.response?.data?.detail || '')
      setUploadError(detailMessage || 'Attachment upload failed. Please retry.')
    } finally {
      setUploadingAttachment(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [channelId, loadAssets, loadThread, onRefreshConversations, uploadingAttachment])

  const onMessageViewportScroll = useCallback((event) => {
    const viewport = messageViewportRef.current
    if (!viewport) return
    const distanceToBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
    shouldStickToBottomRef.current = distanceToBottom < 140
    const trustedScroll = Boolean(event?.isTrusted)
    if (!trustedScroll) return
    if (distanceToBottom < 140) {
      markLatestAsRead(messages).catch(() => { })
    }
  }, [markLatestAsRead, messages])

  const isGroup = String(channel?.channel_type || '').toUpperCase() === 'GROUP'

  const searchAddMemberUsers = useCallback(async (query) => {
    if (!query.trim()) { setAddMemberResults([]); return }
    setAddMemberLoading(true)
    setAddMemberError('')
    try {
      const data = await fetchUsersForThread(query)
      const allUsers = Array.isArray(data?.items) ? data.items : []
      const existingIds = new Set(members.map((m) => Number(m?.id || 0)))
      setAddMemberResults(allUsers.filter((u) => !existingIds.has(Number(u?.id || 0))))
    } catch {
      setAddMemberError('Search failed. Please retry.')
    } finally {
      setAddMemberLoading(false)
    }
  }, [members])

  const handleAddMember = useCallback(async (userId) => {
    if (!channelId) return
    setAddMemberError('')
    setAddMemberSuccess('')
    try {
      await addChannelMember(channelId, userId)
      setAddMemberSuccess('Member added!')
      setAddMemberResults((prev) => prev.filter((u) => Number(u?.id) !== userId))
      await loadThread()
      onRefreshConversations?.()
      setTimeout(() => setAddMemberSuccess(''), 2500)
    } catch (err) {
      const detail = String(err?.response?.data?.detail?.message || err?.response?.data?.detail || '')
      setAddMemberError(detail || 'Failed to add member.')
    }
  }, [channelId, loadThread, onRefreshConversations])

  const handleRemoveMember = useCallback(async (userId) => {
    if (!channelId) return
    setRemovingMemberId(userId)
    try {
      await removeChannelMember(channelId, userId)
      await loadThread()
      onRefreshConversations?.()
    } catch (err) {
      const detail = String(err?.response?.data?.detail?.message || err?.response?.data?.detail || '')
      // eslint-disable-next-line no-console
      console.error('Remove member failed:', detail)
    } finally {
      setRemovingMemberId(null)
    }
  }, [channelId, loadThread, onRefreshConversations])

  return (
    <div className="communication-stitch bg-background text-on-surface h-full min-h-0 flex overflow-hidden rounded-lg border border-surface-variant/20">
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="bg-surface-container-low px-10 py-3 flex items-center justify-between gap-4 border-b border-outline-variant/15">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-outline-variant/20">
              {isDirect && directTargetAvatar ? (
                <img className="w-full h-full rounded-xl object-cover" src={directTargetAvatar} alt={directTargetName || 'User'} />
              ) : isDirect ? (
                <span className="font-bold text-on-surface-variant text-sm">{fallbackInitials(directTargetName || 'User')}</span>
              ) : (
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  architecture
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              {isEditingName ? (
                <div className="flex items-center gap-2 max-w-md relative">
                  <input
                    type="text"
                    className="bg-surface-container-high rounded-xl pl-3 pr-20 py-1.5 text-lg font-bold text-on-surface outline-none focus:ring-2 focus:ring-primary/40 border border-outline-variant/10 w-full"
                    value={editNameText}
                    onChange={(e) => setEditNameText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        bypassBlurRef.current = true
                        handleSaveChannelName().catch(() => {})
                      }
                      if (e.key === 'Escape') {
                        bypassBlurRef.current = true
                        setIsEditingName(false)
                        setEditNameText(lastSavedNameRef.current)
                      }
                    }}
                    onBlur={() => {
                      if (bypassBlurRef.current) {
                        bypassBlurRef.current = false
                        return
                      }
                      handleSaveChannelName().catch(() => {})
                    }}
                    autoFocus
                  />
                  <div className="absolute right-1.5 flex items-center gap-1">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        bypassBlurRef.current = true
                      }}
                      onClick={() => {
                        handleSaveChannelName().catch(() => {})
                      }}
                      className="text-primary hover:bg-primary/10 rounded-full p-1 flex items-center justify-center transition-all"
                      title="Save (Enter)"
                    >
                      <span className="material-symbols-outlined text-xl">check</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        bypassBlurRef.current = true
                      }}
                      onClick={() => {
                        setIsEditingName(false)
                        setEditNameText(lastSavedNameRef.current)
                      }}
                      className="text-on-surface-variant hover:bg-surface-container-highest rounded-full p-1 flex items-center justify-center transition-all"
                      title="Cancel (Esc)"
                    >
                      <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 group/title">
                  <h1 className="text-2xl leading-none font-extrabold font-headline text-on-surface truncate">
                    {isDirect ? directTargetName : (channel?.name || 'Conversation')}
                  </h1>
                  {!isDirect && isChannelAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingName(true)
                        setEditNameText(channel?.name || '')
                      }}
                      className="opacity-0 group-hover/title:opacity-100 text-on-surface-variant hover:text-primary transition-all p-1"
                      title="Rename channel"
                    >
                      <span className="material-symbols-outlined text-lg">edit</span>
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <span className="px-2 py-0.5 bg-primary-fixed text-on-primary-fixed text-[10px] font-bold rounded-full uppercase tracking-tighter">
                  {isDirect ? 'DIRECT MESSAGE' : isGroup ? 'GROUP CHANNEL' : 'PROJECT CHANNEL'}
                </span>
                <span className="text-xs text-on-surface-variant font-medium flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">schedule</span>
                  {updatedLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex items-center gap-2 px-5 py-3 bg-surface-container-lowest text-on-surface text-sm font-semibold rounded-xl hover:bg-white transition-all shadow-sm"
              onClick={onOpenScheduleMeeting}
            >
              <span className="material-symbols-outlined text-lg">event</span>
              Schedule Meeting
            </button>
            <button
              type="button"
              className="px-5 py-3 bg-surface-container-lowest text-on-surface text-sm font-semibold rounded-xl hover:bg-white transition-all shadow-sm"
              onClick={handleBackToHub}
            >
              Back to Hub
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex overflow-hidden">
          <section className="flex-1 min-h-0 flex flex-col bg-surface-container-lowest border-r border-outline-variant/10 relative">
            <div
              ref={messageViewportRef}
              onScroll={onMessageViewportScroll}
              className="flex-1 overflow-y-auto communication-scroll px-8 pt-6 pb-52 space-y-7"
            >
              {loading ? <p className="text-sm text-on-surface-variant">Loading messages...</p> : null}
              {!loading && messages.length === 0 ? <p className="text-sm text-on-surface-variant">No messages yet.</p> : null}

              {timelineRows.map((row) => {
                if (row.type === 'day') {
                  return (
                    <div key={row.key} className="flex items-center gap-4">
                      <div className="flex-1 h-px bg-outline-variant/20"></div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">{row.label}</span>
                      <div className="flex-1 h-px bg-outline-variant/20"></div>
                    </div>
                  )
                }

                const msg = row.message
                const isOwn = Number(msg?.sender_id) === Number(currentUserId)
                const isFileMessage = String(msg?.message_type || '').toUpperCase() === 'FILE'
                const fileHref = resolveMediaUrl(msg?.file_path)
                const messageId = Number(msg?.id || 0)
                const readByMembers = recipientMembers.filter((member) => Number(member?.lastReadMsgId || 0) >= messageId)
                const allRecipientsRead = recipientMembers.length > 0 && readByMembers.length === recipientMembers.length
                return (
                  <div key={row.key}>
                    {unreadMarker?.messageId && Number(unreadMarker.messageId) === messageId && !isOwn ? (
                      <div className="flex justify-center pb-2">
                        <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold">
                          {Number(unreadMarker?.count || 0)} unread messages
                        </div>
                      </div>
                    ) : null}

                    <div ref={(node) => setMessageNodeRef(messageId, node)} className={`flex gap-4 max-w-4xl ${isOwn ? 'flex-row-reverse ml-auto' : ''}`}>
                      <div className="mt-1">
                        <AvatarBadge
                          src={msg?.sender_avatar}
                          name={msg?.sender_name || (isOwn ? currentUserName : 'User')}
                          icon="initials"
                        />
                      </div>
                      <div className={`space-y-1.5 ${isOwn ? 'text-right' : ''}`}>
                        <div className={`flex items-center gap-2 ${isOwn ? 'justify-end' : ''}`}>
                          {!isOwn ? <span className="font-bold text-sm">{msg?.sender_name || 'User'}</span> : null}
                          <span className="text-[11px] text-on-surface-variant/70 font-medium">{formatMessageTime(msg?.created_at)}</span>
                          {isOwn ? <span className="font-bold text-sm">{currentUserName}</span> : null}
                        </div>
                        <div
                          className={
                            isOwn
                              ? 'message-gradient px-5 py-4 rounded-l-xl rounded-br-xl text-on-primary text-[15px] leading-relaxed text-left break-words'
                              : 'bg-surface-container-low px-5 py-4 rounded-r-xl rounded-bl-xl text-on-surface text-[15px] leading-relaxed break-words'
                          }
                        >
                          {isFileMessage ? (
                            <a
                              href={fileHref || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className={`${isOwn ? 'text-on-primary' : 'text-primary'} inline-flex items-center gap-2 underline`}
                            >
                              <span className="material-symbols-outlined text-base">attach_file</span>
                              <span>{msg?.file_name || msg?.content || 'Attachment'}</span>
                            </a>
                          ) : (
                            renderFormattedMessage(msg?.content || '')
                          )}
                        </div>
                        {isOwn ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-[11px] text-on-surface-variant hover:text-on-surface transition-colors"
                            onClick={() => setExpandedReadMessageId((prev) => (prev === messageId ? 0 : messageId))}
                            title="View read status"
                          >
                            <span
                              className={`material-symbols-outlined text-[14px] ${allRecipientsRead ? 'text-primary' : 'text-on-surface-variant'}`}
                              style={{ fontVariationSettings: "'FILL' 1" }}
                            >
                              done_all
                            </span>
                            <span>{allRecipientsRead ? 'Read by all' : 'Delivered'}</span>
                          </button>
                        ) : null}
                        {isOwn ? (
                          <div className={`overflow-hidden transition-all duration-300 ${expandedReadMessageId === messageId ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="mt-1 rounded-xl border border-outline-variant/20 bg-surface-container-low px-3 py-2 text-left">
                              <p className="text-[11px] font-semibold text-on-surface-variant mb-1">Read by</p>
                              {readByMembers.length ? (
                                <div className="space-y-1">
                                  {readByMembers.map((member) => (
                                    <div key={`${messageId}-read-${member.id}`} className="text-xs text-on-surface flex items-center justify-between gap-2">
                                      <span>{member.name || 'User'}</span>
                                      <span className="text-on-surface-variant">seen</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-on-surface-variant">No recipients have read this yet.</p>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* <div className="communication-composer-shell absolute bottom-0 left-0 right-0 px-2 pb-2 pt-2"> */}
            <div className="absolute bottom-0 left-0 right-0 px-2 pb-2 pt-2">
              <div className="min-h-[18px] mb-1">
                {typingUsers.length ? (
                  <p className="text-xs text-on-surface-variant font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                    {formatTypingUsersLabel(typingUsers)}
                  </p>
                ) : null}
              </div>

              <div className="glass-panel border border-outline-variant/30 rounded-[2rem] px-2 py-0 shadow-sm">
                <div className="flex items-center gap-1 px-1 pb-2 border-b border-outline-variant/10">
                  <button
                    type="button"
                    className={`p-1.5 rounded transition-colors ${boldActive ? 'bg-primary text-white' : 'text-on-surface-variant hover:bg-slate-100'}`}
                    onClick={() => setBoldActive((prev) => !prev)}
                    title="Bold"
                  >
                    <span className="material-symbols-outlined text-lg">format_bold</span>
                  </button>
                  <button
                    type="button"
                    className={`p-1.5 rounded transition-colors ${italicActive ? 'bg-primary text-white' : 'text-on-surface-variant hover:bg-slate-100'}`}
                    onClick={() => setItalicActive((prev) => !prev)}
                    title="Italic"
                  >
                    <span className="material-symbols-outlined text-lg">format_italic</span>
                  </button>
                  <div className="h-4 w-px bg-outline-variant/20 mx-1"></div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                    onChange={(event) => { handleAttachmentPick(event).catch(() => { }) }}
                  />
                  <button
                    type="button"
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors text-on-surface-variant"
                    title="Attachment"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                  >
                    <span className="material-symbols-outlined text-lg">attach_file</span>
                  </button>
                  <button
                    type="button"
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors text-on-surface-variant"
                    title="Mention"
                    onClick={() => insertAtCursor('@')}
                    disabled={isDirect}
                  >
                    <span className="material-symbols-outlined text-lg">alternate_email</span>
                  </button>
                  <button
                    type="button"
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors text-on-surface-variant"
                    title="Emoji"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                  >
                    <span className="material-symbols-outlined text-lg">mood</span>
                  </button>
                </div>

                <div className="flex items-end gap-3 pt-2 px-1">
                  <textarea
                    ref={textAreaRef}
                    className="flex-1 bg-transparent border-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 text-[15px] py-2 resize-none min-h-[44px] max-h-28"
                    placeholder="Type a message to the team..."
                    rows={1}
                    value={text}
                    onChange={(event) => {
                      const next = event.target.value
                      setText(next)
                      handleTypingChange(next)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        submitMessage().catch(() => { })
                      }
                      if (!isDirect && showMentionMenu && event.key === 'Tab') {
                        event.preventDefault()
                        const selected = mentionSuggestions[0]
                        if (selected?.name) insertMention(selected.name)
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="communication-send-btn"
                    onClick={() => submitMessage().catch(() => { })}
                    disabled={sending || !text.trim()}
                    title="Send"
                  >
                    <span className="material-symbols-outlined">send</span>
                  </button>
                </div>
                {showMentionMenu ? (
                  <div className="mt-2 rounded-xl border border-outline-variant/20 bg-surface-container-low p-2 max-h-48 overflow-y-auto communication-scroll">
                    {mentionSuggestions.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface-container-high text-sm"
                        onClick={() => insertMention(member?.name || 'User')}
                      >
                        @{member?.name || 'User'}
                      </button>
                    ))}
                  </div>
                ) : null}
                {showEmojiPicker ? (
                  <div className="mt-2 rounded-xl border border-outline-variant/20 bg-surface-container-low p-2">
                    <div className="grid grid-cols-6 gap-2">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="text-xl leading-none p-1.5 rounded-lg hover:bg-surface-container-high"
                          onClick={() => {
                            insertAtCursor(emoji)
                            setShowEmojiPicker(false)
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {uploadError ? (
                  <p className="mt-2 text-xs text-red-600 font-medium">{uploadError}</p>
                ) : null}
              </div>
            </div>
          </section>

          <aside className="w-80 hidden xl:flex flex-col min-h-0 bg-surface-container-lowest">
            <div className="flex-1 min-h-0 overflow-y-auto communication-scroll px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold font-headline text-2xl text-on-surface">Participants</h3>
                {!isDirect && isChannelAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMember((v) => !v)
                      setAddMemberQuery('')
                      setAddMemberResults([])
                      setAddMemberError('')
                      setAddMemberSuccess('')
                    }}
                    className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors"
                    title="Add member to channel"
                  >
                    <span className="material-symbols-outlined text-base">person_add</span>
                    Add
                  </button>
                )}
              </div>

              {!isDirect && isChannelAdmin && showAddMember && (
                <div className="bg-surface-container rounded-2xl p-3 mb-4 space-y-2">
                  <input
                    type="text"
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm text-on-surface placeholder-on-surface-variant/60 outline-none focus:ring-2 focus:ring-primary/40 transition"
                    placeholder="Search by name or email…"
                    value={addMemberQuery}
                    onChange={(e) => {
                      const q = e.target.value
                      setAddMemberQuery(q)
                      searchAddMemberUsers(q)
                    }}
                    autoFocus
                  />
                  {addMemberLoading && (
                    <p className="text-xs text-on-surface-variant/60 px-1">Searching…</p>
                  )}
                  {addMemberError && (
                    <p className="text-xs text-error px-1">{addMemberError}</p>
                  )}
                  {addMemberSuccess && (
                    <p className="text-xs text-tertiary px-1 font-semibold">{addMemberSuccess}</p>
                  )}
                  {addMemberResults.length > 0 && (
                    <ul className="space-y-1 max-h-40 overflow-y-auto communication-scroll">
                      {addMemberResults.map((u) => (
                        <li key={u.id} className="flex items-center gap-2 px-1 py-1 rounded-xl hover:bg-surface-container-high transition-colors">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-bold text-primary shrink-0">
                            {String(u?.name || u?.email || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-on-surface truncate">{u?.name || u?.email || 'User'}</p>
                            {u?.role_title && <p className="text-[10px] text-on-surface-variant truncate">{u.role_title}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddMember(Number(u.id))}
                            className="shrink-0 text-[10px] font-bold text-primary bg-primary/10 hover:bg-primary/20 px-2 py-1 rounded-lg transition-colors"
                          >
                            Add
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!addMemberLoading && addMemberQuery.trim() && addMemberResults.length === 0 && !addMemberError && (
                    <p className="text-xs text-on-surface-variant/60 px-1">No matching users found.</p>
                  )}
                </div>
              )}

              <div className="space-y-4 mb-8">
                {memberReadStates.map((member) => {
                  const memberOnline = Boolean(presenceByUser[Number(member?.id || 0)]?.isOnline)
                  const isRemoving = removingMemberId === Number(member?.id)
                  return (
                    <div className="flex items-center gap-3 group" key={member.id}>
                      <AvatarBadge
                        src={member?.avatar}
                        name={member?.name || 'User'}
                        icon="initials"
                        sizeClass="w-12 h-12"
                        showPresence
                        isOnline={memberOnline}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-on-surface">{member?.name || 'User'}</p>
                        <p className="text-xs text-on-surface-variant">
                          {member?.role || 'MEMBER'} | {memberOnline ? 'Online' : 'Offline'}
                        </p>
                      </div>
                      {!isDirect && isChannelAdmin && Number(member?.id) !== currentUserId && (
                        <button
                          type="button"
                          title="Remove from channel"
                          disabled={isRemoving}
                          onClick={() => handleRemoveMember(Number(member.id))}
                          className="shrink-0 opacity-0 group-hover:opacity-100 text-error hover:bg-error/10 rounded-full p-1 transition-all disabled:opacity-50"
                        >
                          {isRemoving
                            ? <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                            : <span className="material-symbols-outlined text-base">person_remove</span>
                          }
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>


              <h3 className="font-bold font-headline text-2xl text-on-surface mb-4">Shared Assets</h3>
              {assetsLoading && assets.length === 0 ? <p className="text-xs text-on-surface-variant mb-3">Loading assets...</p> : null}
              {!assetsLoading && assets.length === 0 ? <p className="text-xs text-on-surface-variant mb-3">No shared assets yet.</p> : null}
              {assets.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {assets.map((asset) => {
                    const href = resolveMediaUrl(asset?.file_path)
                    const isImage = isImageAsset(asset?.file_name || asset?.file_path)
                    return (
                      <a
                        key={asset.id}
                        href={href || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="aspect-square bg-surface-container rounded-2xl overflow-hidden flex items-center justify-center"
                        title={asset?.file_name || 'Attachment'}
                      >
                        {isImage && href ? (
                          <img className="w-full h-full object-cover" alt={asset?.file_name || 'asset'} src={href} />
                        ) : (
                          <div className="h-full w-full p-3 flex flex-col items-center justify-center gap-2 text-center">
                            <span className="material-symbols-outlined text-primary">description</span>
                            <span className="text-[11px] leading-tight text-on-surface-variant line-clamp-3">{asset?.file_name || 'Attachment'}</span>
                          </div>
                        )}
                      </a>
                    )
                  })}
                  {assets.length < assetsTotal ? (
                    <button
                      type="button"
                      className="aspect-square bg-surface-container rounded-2xl flex items-center justify-center text-sm font-bold text-on-surface-variant hover:bg-surface-container-high transition-colors"
                      onClick={() => loadAssets(assetsPage + 1, true).catch(() => { })}
                      disabled={assetsLoading}
                      title="Load more assets"
                    >
                      {assetsLoading ? '...' : `+${Math.min(ASSETS_PAGE_SIZE, assetsTotal - assets.length)} more`}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </main>
    </div>
  )
}

function ConversationHubScreen({
  conversations,
  conversationsLoading,
  search,
  setSearch,
  contextProjectId,
  setContextProjectId,
  includeDirect,
  setIncludeDirect,
  includeProject,
  setIncludeProject,
  unreadOnly,
  setUnreadOnly,
  onOpenConversation,
  currentUserId,
  currentUserName,
  page,
  onPageChange,
  hubKpis,
  hubKpisLoading,
  onComposeClick,
}) {
  const contextOptions = useMemo(() => {
    const items = conversations
      .filter((item) => String(item?.channel_type || '').toUpperCase() === 'PROJECT')
      .map((item) => ({ value: String(item?.project_id || ''), label: item?.channel_name || 'Project Channel' }))
      .filter((item) => item.value)
    const unique = Array.from(new Map(items.map((item) => [item.value, item])).values())
    return [{ value: 'ALL', label: 'All Projects' }, ...unique]
  }, [conversations])

  const filteredConversations = useMemo(() => {
    return conversations.filter((item) => {
      const kind = String(item?.channel_type || '').toUpperCase()
      if (!includeDirect && kind === 'DIRECT') return false
      if (!includeProject && (kind === 'PROJECT' || kind === 'GROUP')) return false
      if (unreadOnly && Number(item?.unread_count || 0) <= 0) return false
      if (contextProjectId !== 'ALL' && String(item?.project_id || '') !== contextProjectId) return false
      if (search.trim()) {
        const needle = search.trim().toLowerCase()
        const hay = `${toConversationName(item)} ${item?.last_message_content || ''}`.toLowerCase()
        if (!hay.includes(needle)) return false
      }
      return true
    })
  }, [conversations, includeDirect, includeProject, unreadOnly, contextProjectId, search])

  const totalPages = Math.max(1, Math.ceil(filteredConversations.length / HUB_PAGE_SIZE))
  const safePage = Math.min(page, totalPages)

  useEffect(() => {
    if (page > totalPages) onPageChange(totalPages)
  }, [onPageChange, page, totalPages])

  const pagedConversations = useMemo(() => {
    const start = (safePage - 1) * HUB_PAGE_SIZE
    return filteredConversations.slice(start, start + HUB_PAGE_SIZE)
  }, [filteredConversations, safePage])

  const [presenceByUser, setPresenceByUser] = useState({})
  const directPresenceUserIds = useMemo(
    () => Array.from(
      new Set(
        pagedConversations
          .filter((item) => String(item?.channel_type || '').toUpperCase() === 'DIRECT')
          .map((item) => Number(item?.other_user_id || 0))
          .filter((id) => id > 0),
      ),
    ),
    [pagedConversations],
  )
  const directPresenceUserIdsKey = useMemo(
    () => directPresenceUserIds.join(','),
    [directPresenceUserIds],
  )

  const refreshHubPresence = useCallback(async () => {
    if (!directPresenceUserIds.length) {
      setPresenceByUser((prev) => (Object.keys(prev).length ? {} : prev))
      return
    }
    try {
      const data = await fetchChatPresence(directPresenceUserIds)
      const rows = Array.isArray(data?.items) ? data.items : []
      const next = {}
      rows.forEach((item) => {
        const uid = Number(item?.userId || 0)
        if (!uid) return
        next[uid] = {
          isOnline: Boolean(item?.isOnline),
          lastHeartbeatAt: item?.lastHeartbeatAt || null,
        }
      })
      setPresenceByUser((prev) => (arePresenceMapsEqual(prev, next) ? prev : next))
    } catch {
      setPresenceByUser((prev) => (Object.keys(prev).length ? {} : prev))
    }
  }, [directPresenceUserIds, directPresenceUserIdsKey])

  const unreadTotal = useMemo(
    () => filteredConversations.reduce((acc, row) => acc + Number(row?.unread_count || 0), 0),
    [filteredConversations],
  )

  useEffect(() => {
    refreshHubPresence().catch(() => { })
  }, [refreshHubPresence])

  useEffect(() => {
    if (!directPresenceUserIds.length) return undefined
    const timer = setInterval(() => {
      refreshHubPresence().catch(() => { })
    }, PRESENCE_POLL_MS)
    return () => clearInterval(timer)
  }, [directPresenceUserIds.length, directPresenceUserIdsKey, refreshHubPresence])

  return (
    <div className="p-8 communication-stitch bg-background text-on-background">
      <div className="mb-10">
        <h2 className="text-4xl font-headline font-extrabold text-on-background tracking-tight mb-2">Communication Hub</h2>
        <p className="text-on-surface-variant text-lg max-w-2xl">Unified workspace for project discussions, team coordination, and enterprise-wide updates.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-3 space-y-6">
          <div className="bg-surface-container-low p-6 rounded-lg">
            <h3 className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-4 font-label">Conversation Filters</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-on-surface">Context</label>
                <AppDropdown
                  value={contextProjectId}
                  onChange={(value) => setContextProjectId(String(value))}
                  options={contextOptions}
                  triggerClassName="w-full rounded-md border-none bg-surface-container-lowest text-sm !focus-visible:ring-0 focus-visible:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-on-surface">Type</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <input
                      checked={includeDirect}
                      className="communication-type-checkbox h-4 w-4 rounded-sm border border-outline-variant accent-[#0f8fa4] focus:ring-0 focus:outline-none"
                      type="checkbox"
                      onChange={(event) => setIncludeDirect(event.target.checked)}
                    />
                    <span className="text-sm text-on-surface-variant group-hover:text-primary transition-colors">Direct Messages</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <input
                      checked={includeProject}
                      className="communication-type-checkbox h-4 w-4 rounded-sm border border-outline-variant accent-[#0f8fa4] focus:ring-0 focus:outline-none"
                      type="checkbox"
                      onChange={(event) => setIncludeProject(event.target.checked)}
                    />
                    <span className="text-sm text-on-surface-variant group-hover:text-primary transition-colors">Project Channels</span>
                  </label>
                </div>
              </div>
              <button type="button" className="w-full py-2.5 bg-primary-container text-on-primary rounded-lg text-sm font-bold mt-4 hover:opacity-90 transition-opacity">
                Apply Filters
              </button>
            </div>
          </div>

          <div className="bg-primary bg-gradient-to-br from-primary to-primary-container p-6 rounded-[2rem] text-on-primary relative overflow-hidden group min-h-[200px]">
            <div className="relative z-10">
              <h3 className="font-headline font-bold text-2xl leading-[1.02] mb-3">New Conversation</h3>
              <p className="text-on-primary-container text-sm mb-6 max-w-[220px]">Start a new thread or create a project channel to get things moving.</p>
              <button
                type="button"
                onClick={onComposeClick}
                className="bg-white text-primary px-6 py-2 rounded-xl font-bold text-sm inline-flex items-center space-x-2"
              >
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'wght' 700" }}>add</span>
                <span>Compose</span>
              </button>
            </div>
            <div className="absolute right-3 bottom-3 w-44 h-20 bg-white/15 rounded-lg rotate-12"></div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-9 bg-surface-container-lowest rounded-lg overflow-hidden border border-surface-variant/20">
          <div className="p-6 border-b border-surface-variant bg-surface-container-low flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant font-label">Active Conversations</span>
              <span className="bg-primary text-on-primary text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadTotal} Unread</span>
            </div>
            <button
              type="button"
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${unreadOnly
                  ? 'bg-primary text-on-primary border-primary'
                  : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant/30'
                }`}
              onClick={() => setUnreadOnly((prev) => !prev)}
            >
              {unreadOnly ? 'Unread Only: ON' : 'Unread Only'}
            </button>
          </div>

          <div className="p-4 border-b border-surface-variant/30">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-base">search</span>
              <input
                className="w-full bg-surface-container-low border-none rounded-full pl-10 pr-4 py-2 text-sm focus:ring-0 focus:outline-none transition-all"
                placeholder="Search conversations..."
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-12 px-6 py-4 bg-surface-container-low/50 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/70 font-label">
            <div className="col-span-4">Conversation &amp; Context</div>
            <div className="col-span-4">Last Message Snippet</div>
            <div className="col-span-2 text-center">Unread</div>
            <div className="col-span-2 text-right">Last Activity</div>
          </div>

          <div className="space-y-1 p-2 min-h-[280px]">
            {conversationsLoading ? <p className="p-3 text-sm text-on-surface-variant">Loading conversations...</p> : null}
            {!conversationsLoading && pagedConversations.length === 0 ? <p className="p-3 text-sm text-on-surface-variant">No conversations found.</p> : null}
            {pagedConversations.map((item) => {
              const nextChannelId = Number(item?.channel_id || 0)
              const hasUnread = Number(item?.unread_count || 0) > 0
              const isDirectConversation = String(item?.channel_type || '').toUpperCase() === 'DIRECT'
              const directUserId = Number(item?.other_user_id || 0)
              return (
                <button
                  key={nextChannelId}
                  type="button"
                  className="w-full text-left grid grid-cols-12 items-center px-4 py-4 rounded-lg hover:bg-surface-container-low cursor-pointer transition-colors group"
                  onClick={() => onOpenConversation(nextChannelId)}
                >
                  <div className="col-span-4 flex items-center space-x-4">
                    <AvatarBadge
                      src={toConversationAvatar(item)}
                      name={toConversationName(item)}
                      icon={isDirectConversation ? 'initials' : 'groups'}
                      showPresence={isDirectConversation}
                      isOnline={Boolean(presenceByUser[directUserId]?.isOnline)}
                    />
                    <div>
                      <p className={`text-sm ${hasUnread ? 'font-extrabold text-on-surface' : 'font-bold text-on-surface'}`}>{toConversationName(item)}</p>
                      <p className="text-xs text-on-surface-variant">{toConversationType(item)}</p>
                    </div>
                  </div>
                  <div className="col-span-4">
                    <p className={`truncate text-sm ${snippetFromSelf(item, currentUserId) ? 'italic' : ''} ${hasUnread ? 'font-semibold text-on-surface' : 'text-on-surface-variant'}`}>
                      {toSnippet(item, currentUserId, currentUserName)}
                    </p>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    {hasUnread ? (
                      <span className="w-6 h-6 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                        {Number(item?.unread_count || 0)}
                      </span>
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-surface-variant text-on-surface-variant text-[10px] font-bold flex items-center justify-center">0</span>
                    )}
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-xs font-bold text-on-surface">{formatClock(item?.last_message_at)}</p>
                    <p className="text-[10px] text-on-surface-variant">Recent</p>
                  </div>
                </button>
              )
            })}
          </div>

          <div className="p-6 bg-surface-container-low/30 border-t border-surface-variant">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-on-surface-variant font-medium">Showing {pagedConversations.length} of {filteredConversations.length} conversations</p>
              <div className="min-w-[240px]">
                <AppPagination
                  page={safePage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  onPrevious={() => onPageChange(Math.max(1, safePage - 1))}
                  onNext={() => onPageChange(Math.min(totalPages, safePage + 1))}
                  canPrevious={safePage > 1}
                  canNext={safePage < totalPages}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-surface-container-lowest p-6 rounded-lg flex items-center space-x-6 border border-white/50">
            <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl">chat_bubble</span>
            </div>
            <div>
              <p className="text-3xl font-headline font-black text-on-background">
                {hubKpisLoading ? '...' : Number(hubKpis?.messagesToday || 0)}
              </p>
              <p className="text-sm font-bold text-on-surface-variant uppercase tracking-wide">Messages Today</p>
            </div>
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-lg flex items-center space-x-6 border border-white/50">
            <div className="w-12 h-12 bg-green-100 text-green-700 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl">done_all</span>
            </div>
            <div>
              <p className="text-3xl font-headline font-black text-on-background">
                {hubKpisLoading ? '...' : `${Number(hubKpis?.responseRatePercent || 0)}%`}
              </p>
              <p className="text-sm font-bold text-on-surface-variant uppercase tracking-wide">Response Rate</p>
            </div>
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-lg flex items-center space-x-6 border border-white/50">
            <div className="w-12 h-12 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl">avg_time</span>
            </div>
            <div>
              <p className="text-3xl font-headline font-black text-on-background">
                {hubKpisLoading ? '...' : `${Number(hubKpis?.avgReplyMinutes || hubKpis?.avgReplyMinutesMedian || 0)}m`}
              </p>
              <p className="text-sm font-bold text-on-surface-variant uppercase tracking-wide">Avg. Reply Time</p>
            </div>
          </div>
        </div>
      </div>

      {/* <button className="fixed bottom-8 right-8 w-16 h-16 bg-primary text-on-primary rounded-full shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all z-40 group" type="button">
        <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'wght' 600" }}>edit</span>
      </button> */}
    </div>
  )
}

function ThreadUsersScreen({ users, loading, search, setSearch, onOpenUserThread, page, totalPages, onPageChange, total }) {
  return (
    <div className="p-8 communication-stitch bg-background text-on-background">
      <div className="max-w-5xl">
        <div className="mb-8">
          <h2 className="text-4xl font-headline font-extrabold text-on-background tracking-tight mb-2">Threads</h2>
          <p className="text-on-surface-variant text-lg">Start private user-to-user conversations.</p>
        </div>

        <div className="bg-surface-container-lowest rounded-lg overflow-hidden border border-surface-variant/20">
          <div className="p-5 border-b border-surface-variant/30 bg-surface-container-low">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-base">search</span>
              <input
                className="w-full bg-surface-container-lowest border-none rounded-full pl-10 pr-4 py-2 text-sm focus:ring-0 focus:outline-none transition-all"
                placeholder="Search users..."
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1 p-2 min-h-[320px]">
            {loading ? <p className="p-3 text-sm text-on-surface-variant">Loading users...</p> : null}
            {!loading && users.length === 0 ? <p className="p-3 text-sm text-on-surface-variant">No users found.</p> : null}
            {users.map((person) => (
              <button
                key={person.id}
                type="button"
                className="w-full text-left grid grid-cols-12 items-center px-4 py-4 rounded-lg hover:bg-surface-container-low cursor-pointer transition-colors group"
                onClick={() => onOpenUserThread(person).catch(() => { })}
              >
                <div className="col-span-7 flex items-center space-x-4">
                  <AvatarBadge src={person?.profile_photo_path} name={person?.name || 'User'} icon="initials" />
                  <div>
                    <p className="font-bold text-on-surface text-sm">{person?.name || 'Unknown User'}</p>
                    <p className="text-xs text-on-surface-variant">{person?.email || ''}</p>
                  </div>
                </div>
                <div className="col-span-5 text-right text-xs text-on-surface-variant">
                  {person?.existing_channel_id ? 'Open thread' : 'Start thread'}
                </div>
              </button>
            ))}
          </div>

          <div className="p-4 border-t border-surface-variant/20 flex items-center justify-between gap-3">
            <p className="text-xs text-on-surface-variant">Showing {users.length} of {total} users</p>
            <div className="min-w-[220px]">
              <AppPagination
                page={page}
                totalPages={Math.max(1, totalPages)}
                onPageChange={onPageChange}
                onPrevious={() => onPageChange(Math.max(1, page - 1))}
                onNext={() => onPageChange(Math.min(Math.max(1, totalPages), page + 1))}
                canPrevious={page > 1}
                canNext={page < Math.max(1, totalPages)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CommunicationScreen({ tab = 'hub' }) {
  const navigate = useNavigate()
  const user = useMemo(() => getStoredUser(), [])
  const [searchParams] = useSearchParams()
  const channelId = Number(searchParams.get('channelId') || 0)

  const [conversations, setConversations] = useState([])
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const [hubSearch, setHubSearch] = useState('')
  const [hubPage, setHubPage] = useState(1)

  const [threadUsers, setThreadUsers] = useState([])
  const [threadUsersLoading, setThreadUsersLoading] = useState(false)
  const [threadSearch, setThreadSearch] = useState('')
  const [threadPage, setThreadPage] = useState(1)
  const [threadTotal, setThreadTotal] = useState(0)

  const [contextProjectId, setContextProjectId] = useState('ALL')
  const [includeDirect, setIncludeDirect] = useState(true)
  const [includeProject, setIncludeProject] = useState(true)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [hubKpisLoading, setHubKpisLoading] = useState(false)
  const [hubKpis, setHubKpis] = useState({
    messagesToday: 0,
    responseRatePercent: 0,
    avgReplyMinutes: 0,
    avgReplyMinutesMedian: 0,
  })
  const readAckByChannelRef = useRef(new Map())
  const refreshRequestSeqRef = useRef(0)
  const latestAppliedRefreshSeqRef = useRef(0)

  const [showComposeModal, setShowComposeModal] = useState(false)
  const [composeType, setComposeType] = useState(null)
  const [newGroupName, setNewGroupName] = useState('')
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState('')

  const applyAckToRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return []
    const ackMap = readAckByChannelRef.current
    return rows.map((item) => {
      const channelKey = toSafePositiveInt(item?.channel_id || item?.channelId || 0)
      if (!channelKey) return item
      const ackMessageId = toSafePositiveInt(ackMap.get(channelKey) || 0)
      if (!ackMessageId) return item
      const lastMessageId = getConversationLastMessageId(item)
      if (lastMessageId > 0 && lastMessageId <= ackMessageId && Number(item?.unread_count || 0) > 0) {
        return {
          ...item,
          unread_count: 0,
        }
      }
      return item
    })
  }, [])

  const noteReadAck = useCallback((targetChannelId, lastReadMessageId) => {
    const channelKey = toSafePositiveInt(targetChannelId || 0)
    const readPointer = toSafePositiveInt(lastReadMessageId || 0)
    if (!channelKey || !readPointer) return

    const current = toSafePositiveInt(readAckByChannelRef.current.get(channelKey) || 0)
    if (readPointer <= current) return
    readAckByChannelRef.current.set(channelKey, readPointer)

    setConversations((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev
      return prev.map((item) => {
        const rowChannelId = toSafePositiveInt(item?.channel_id || item?.channelId || 0)
        if (rowChannelId !== channelKey) return item
        const lastMessageId = getConversationLastMessageId(item)
        if (lastMessageId > 0 && lastMessageId <= readPointer && Number(item?.unread_count || 0) > 0) {
          return {
            ...item,
            unread_count: 0,
          }
        }
        return item
      })
    })
  }, [])

  const refreshConversations = useCallback(async () => {
    const requestSeq = ++refreshRequestSeqRef.current
    setConversationsLoading(true)
    try {
      const data = await fetchConversations({ page: 1, pageSize: 100 })
      if (requestSeq < latestAppliedRefreshSeqRef.current) return
      latestAppliedRefreshSeqRef.current = requestSeq
      const nextRows = applyAckToRows(Array.isArray(data?.items) ? data.items : [])
      setConversations(nextRows)
      window.dispatchEvent(new CustomEvent('chat-unread-updated'))
    } finally {
      if (requestSeq === refreshRequestSeqRef.current) {
        setConversationsLoading(false)
      }
    }
  }, [applyAckToRows])

  useEffect(() => {
    refreshConversations().catch(() => { })
  }, [refreshConversations])

  useEffect(() => {
    const handleGlobalWsEvent = (event) => {
      const payload = event?.detail
      if (!payload || typeof payload !== 'object') return
      if (
        payload?.event === 'new_message' ||
        payload?.event === 'read_receipt' ||
        payload?.event === 'member_added' ||
        payload?.event === 'member_removed' ||
        payload?.event === 'channel_renamed'
      ) {
        refreshConversations().catch(() => { })
      }
    }
    window.addEventListener('chat-ws-event', handleGlobalWsEvent)
    return () => window.removeEventListener('chat-ws-event', handleGlobalWsEvent)
  }, [refreshConversations])

  useEffect(() => {
    setHubPage(1)
  }, [hubSearch, contextProjectId, includeDirect, includeProject, unreadOnly])

  useEffect(() => {
    setThreadPage(1)
  }, [threadSearch])

  useEffect(() => {
    setHubKpisLoading(true)
    fetchChatHubKpis({
      includeDirect,
      includeProject,
      contextProjectId: contextProjectId === 'ALL' ? undefined : Number(contextProjectId),
    })
      .then((data) => {
        setHubKpis({
          messagesToday: Number(data?.messagesToday || 0),
          responseRatePercent: Number(data?.responseRatePercent || 0),
          avgReplyMinutes: Number(data?.avgReplyMinutes || 0),
          avgReplyMinutesMedian: Number(data?.avgReplyMinutesMedian || data?.avgReplyMinutes || 0),
        })
      })
      .catch(() => {
        setHubKpis({
          messagesToday: 0,
          responseRatePercent: 0,
          avgReplyMinutes: 0,
          avgReplyMinutesMedian: 0,
        })
      })
      .finally(() => setHubKpisLoading(false))
  }, [contextProjectId, includeDirect, includeProject])

  useEffect(() => {
    if (tab !== 'thread' || channelId > 0) return

    setThreadUsersLoading(true)
    fetchUsersForThread({ page: threadPage, pageSize: THREAD_PAGE_SIZE, search: threadSearch })
      .then((data) => {
        setThreadUsers(Array.isArray(data?.items) ? data.items : [])
        setThreadTotal(Number(data?.total || 0))
      })
      .finally(() => setThreadUsersLoading(false))
  }, [tab, channelId, threadSearch, threadPage])

  const openConversation = useCallback((id) => {
    const normalized = Number(id)
    if (!Number.isFinite(normalized) || normalized <= 0) return
    navigate(`/bridge/conversation?channelId=${normalized}`)
  }, [navigate])

  const openUserThread = useCallback(async (person) => {
    const existing = Number(person?.existing_channel_id || person?.existingChannelId || 0)
    if (Number.isFinite(existing) && existing > 0) {
      openConversation(existing)
      return
    }

    const userId = Number(person?.id || 0)
    if (!Number.isFinite(userId) || userId <= 0) return

    const payload = await startDirectThread(userId)
    const newChannelId = Number(payload?.channelId || 0)
    if (Number.isFinite(newChannelId) && newChannelId > 0) {
      await refreshConversations()
      openConversation(newChannelId)
    }
  }, [openConversation, refreshConversations])

  if (tab === 'conversation' && channelId > 0) {
    return (
      <>
        <ConversationThreadScreen
          currentUserId={user.id}
          currentUserName={user.name}
          channelId={channelId}
          onBackToHub={() => navigate('/bridge/hub')}
          onRefreshConversations={refreshConversations}
          onReadAck={noteReadAck}
          onOpenScheduleMeeting={() => setIsScheduleModalOpen(true)}
        />
        <BridgeActionCenterModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          onCreated={() => setIsScheduleModalOpen(false)}
          defaultDate={new Date()}
        />
      </>
    )
  }

  if (tab === 'thread') {
    const threadTotalPages = Math.max(1, Math.ceil(threadTotal / THREAD_PAGE_SIZE))
    return (
      <ThreadUsersScreen
        users={threadUsers}
        loading={threadUsersLoading}
        search={threadSearch}
        setSearch={setThreadSearch}
        onOpenUserThread={openUserThread}
        page={threadPage}
        totalPages={threadTotalPages}
        onPageChange={setThreadPage}
        total={threadTotal}
      />
    )
  }

  return (
    <>
      <ConversationHubScreen
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        search={hubSearch}
        setSearch={setHubSearch}
        contextProjectId={contextProjectId}
        setContextProjectId={setContextProjectId}
        includeDirect={includeDirect}
        setIncludeDirect={setIncludeDirect}
        includeProject={includeProject}
        setIncludeProject={setIncludeProject}
        unreadOnly={unreadOnly}
        setUnreadOnly={setUnreadOnly}
        onOpenConversation={openConversation}
        currentUserId={user.id}
        currentUserName={user.name}
        page={hubPage}
        onPageChange={setHubPage}
        hubKpis={hubKpis}
        hubKpisLoading={hubKpisLoading}
        onComposeClick={() => setShowComposeModal(true)}
      />

      {showComposeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-low rounded-[2rem] w-full max-w-md p-8 border border-outline-variant/20 shadow-2xl relative">
            <button
              type="button"
              onClick={() => {
                setShowComposeModal(false)
                setComposeType(null)
                setNewGroupName('')
                setCreateError('')
              }}
              className="absolute right-6 top-6 text-on-surface-variant hover:text-on-surface transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>

            {!composeType ? (
              <div className="space-y-6">
                <div>
                  <h3 className="font-headline font-extrabold text-2xl text-on-surface mb-2">New Conversation</h3>
                  <p className="text-on-surface-variant text-sm">Choose the type of conversation you want to start.</p>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowComposeModal(false)
                      navigate('/bridge/thread')
                    }}
                    className="flex flex-col items-center justify-center p-6 bg-surface-container-high hover:bg-primary/10 border border-transparent hover:border-primary/30 rounded-2xl transition-all group"
                  >
                    <span className="material-symbols-outlined text-4xl text-primary group-hover:scale-110 transition-transform mb-3">person</span>
                    <span className="font-bold text-sm text-on-surface">Individual</span>
                    <span className="text-[11px] text-on-surface-variant text-center mt-1">Direct Message</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setComposeType('group')}
                    className="flex flex-col items-center justify-center p-6 bg-surface-container-high hover:bg-primary/10 border border-transparent hover:border-primary/30 rounded-2xl transition-all group"
                  >
                    <span className="material-symbols-outlined text-4xl text-primary group-hover:scale-110 transition-transform mb-3">groups</span>
                    <span className="font-bold text-sm text-on-surface">Group Channel</span>
                    <span className="text-[11px] text-on-surface-variant text-center mt-1">Multi-user channel</span>
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  const trimmed = newGroupName.trim()
                  if (!trimmed || createLoading) return
                  setCreateLoading(true)
                  setCreateError('')
                  try {
                    const res = await createGroupChannel(trimmed)
                    const newChannelId = Number(res?.channelId)
                    if (newChannelId > 0) {
                      setShowComposeModal(false)
                      setComposeType(null)
                      setNewGroupName('')
                      await refreshConversations()
                      openConversation(newChannelId)
                    }
                  } catch (err) {
                    const detail = String(err?.response?.data?.detail?.message || err?.response?.data?.detail || '')
                    setCreateError(detail || 'Failed to create group channel')
                  } finally {
                    setCreateLoading(false)
                  }
                }}
                className="space-y-6"
              >
                <div>
                  <h3 className="font-headline font-extrabold text-2xl text-on-surface mb-2">Create Group Channel</h3>
                  <p className="text-on-surface-variant text-sm">Give your group channel a name to get started.</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface">Group Name</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-surface-container-highest rounded-xl px-4 py-3 text-sm text-on-surface placeholder-on-surface-variant/50 outline-none focus:ring-2 focus:ring-primary/40 border border-outline-variant/10 transition"
                    placeholder="e.g. Design Sync, Marketing Funnel"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    autoFocus
                  />
                  {createError && <p className="text-xs text-error">{createError}</p>}
                </div>
                <div className="flex items-center gap-3 pt-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setComposeType(null)
                      setNewGroupName('')
                      setCreateError('')
                    }}
                    className="px-4 py-2 text-sm font-bold text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading || !newGroupName.trim()}
                    className="px-6 py-2.5 bg-primary text-on-primary font-bold rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
